import apiClient from './config'
import { submitPolloModel, pollPolloStatus, getLogicalModelProvider, pollAllallStatus, pollAllallWan25Status, pollAllallMidjourneyStatus, generateAllallImage, generateAllallSpeech, submitSunoMusic, pollSunoMusicStatus, submitWanUnifiedTask, pollWanUnifiedStatus, submitUnifiedDirectImageTask, UNIFIED_DIRECT_IMAGE_MODEL_IDS } from './ai'
import { AI_MODEL_PATHS, isAllallImageModel, isAllallAudioModel, isAllallMusicModel } from '@/config/ai-providers'
import { getOfficialVideoProfile, getOfficialImageProfile, getOfficialMusicProfile } from '@/config/official-channels'
import { buildImageParams, buildVideoParams } from './params/buildRequestParams'
import { getLogicalRoute } from './generation-routes'
import { submitOfficialVideo, pollOfficialVideo } from './ai/official/video'
import { submitOfficialImage } from './ai/official/image'
import { submitOfficialMusic, pollOfficialMusic } from './ai/official/music'

var generationApi = {
  generateImage: function(params) {
    return apiClient.post('/generate/image', buildImageParams(params))
  },

  generateVideo: function(params) {
    return apiClient.post('/generate/video', buildVideoParams(params))
  },

  getGenerationStatus: function(taskId) {
    return apiClient.get('/generate/status/' + taskId)
  },

  getGenerationHistory: function(params) {
    return apiClient.get('/generate/history', { params: params })
  },

  cancelGeneration: function(taskId) {
    return apiClient.post('/generate/cancel/' + taskId)
  }
}

/** Pollo 模型列表：由 config 的 AI_MODEL_PATHS 中 providerId 为 pollo 的项推导，避免重复维护 */
var POLLO_MODELS = Object.keys(AI_MODEL_PATHS || {}).filter(function (id) { return AI_MODEL_PATHS[id].providerId === 'pollo' })

function normalizePolloRes(res) {
  return { taskId: res.taskId, status: res.status, provider: 'pollo' }
}

function normalizeAllallRes(res) {
  return { taskId: res.taskId, provider: res.provider, allallApi: res.allallApi }
}

function mapWanUnifiedImageAsyncSubmit(res) {
  var out = {
    taskId: res.taskId,
    createId: res.createId,
    provider: res.provider,
    wanModelName: res.wanModelName,
    wanModelVersion: res.wanModelVersion,
    wanModelType: res.wanModelType
  }
  /* Flux：create 返回的 pollingUrl，query_state 首轮及后续需回传 */
  if (res.wanPollingUrl) out.wanPollingUrl = res.wanPollingUrl
  return out
}

export function generateContent(params) {
  var modelId = params.modelId
  if (modelId === 'wan-2-2' || modelId === 'wan-2-5' || modelId === 'wan-2-6' || modelId === 'wan-animate' || modelId === 'seedance' || modelId === 'sora-2' || modelId === 'seegen' || modelId === 'pollodance' || modelId === 'runway' || modelId === 'hailuo-2-3' || modelId === 'vidu-q3-pro' || modelId === 'kling-2-5' || modelId === 'kling-2-6' || modelId === 'kling-3' || modelId === 'suno-music' || modelId === 'veo-3-0' || modelId === 'veo-3-1' || modelId === 'veo-3-1-fast') {
    return submitWanUnifiedTask(params).then(function (res) {
      return {
        taskId: res.taskId,
        createId: res.createId,
        provider: res.provider,
        wanModelName: res.wanModelName,
        wanModelVersion: res.wanModelVersion,
        wanModelType: res.wanModelType
      }
    })
  }
  if (UNIFIED_DIRECT_IMAGE_MODEL_IDS.indexOf(modelId) !== -1) {
    return submitUnifiedDirectImageTask(params).then(mapWanUnifiedImageAsyncSubmit)
  }

  var officialVideoProfile = getOfficialVideoProfile(modelId)
  if (officialVideoProfile) {
    return submitOfficialVideo(officialVideoProfile, params).then(function (res) {
      return {
        taskId: res.taskId,
        provider: 'official',
        officialProfile: officialVideoProfile,
        officialMedia: 'video',
        videoMode: res && res.videoMode ? res.videoMode : (params && params.videoMode ? params.videoMode : 'text')
      }
    })
  }

  var officialImageProfile = getOfficialImageProfile(modelId)
  if (officialImageProfile) {
    return submitOfficialImage(officialImageProfile, params).then(function (res) {
      return {
        urls: res.urls,
        blobs: res.blobs,
        files: res.files,
        provider: 'official',
        sync: true,
        officialMedia: 'image',
        officialProfile: officialImageProfile
      }
    })
  }

  var officialMusicProfile = getOfficialMusicProfile(modelId)
  if (officialMusicProfile) {
    return submitOfficialMusic(officialMusicProfile, params).then(function (res) {
      return {
        taskId: res.taskId,
        provider: 'official',
        officialProfile: officialMusicProfile,
        officialMedia: 'music'
      }
    })
  }

  var route = getLogicalRoute(modelId)

  if (route) {
    if (route.allallOnly) {
      if (!route.condition(params)) {
        return Promise.reject(new Error('AllAll Sora 2 仅支持图片转视频，请上传图片'))
      }
      var allallParams = route.transformParams ? route.transformParams(params) : params
      return route.submit(allallParams).then(normalizeAllallRes)
    }
    if (route.allall && getLogicalModelProvider(route.allall.logicalKey) === 'allall' && route.allall.condition(params)) {
      var p = route.allall.transformParams ? route.allall.transformParams(params) : params
      return route.allall.submit(p).then(function (res) {
        return { taskId: res.taskId, provider: res.provider, allallApi: res.allallApi }
      })
    }
    if (route.pollo) {
      return submitPolloModel(route.pollo, params).then(normalizePolloRes)
    }
  }

  if (POLLO_MODELS.indexOf(modelId) !== -1) {
    return submitPolloModel(modelId, params).then(normalizePolloRes)
  }
  if (isAllallImageModel(modelId)) {
    return generateAllallImage(modelId, params)
  }
  if (isAllallAudioModel(modelId)) {
    return generateAllallSpeech(params)
  }
  if (isAllallMusicModel(modelId)) {
    return submitSunoMusic(params).then(function (res) {
      return { taskId: res.taskId, status: res.status, provider: res.provider, allallApi: res.allallApi }
    })
  }
  var isImageModel = params.type === 'image'
  var requestParams = isImageModel ? buildImageParams(params) : buildVideoParams(params)
  var endpoint = isImageModel ? '/generate/image' : '/generate/video'
  return apiClient.post(endpoint, requestParams)
}

/**
 * 轮询任务状态。任务提交后，前端需传入 options.provider（如 'pollo'）以走对应渠道：
 * - provider === 'pollo'：轮询 GET /generation/{taskId}/status（Pollo 通用）
 * - provider === 'allall'：按 allallApi 轮询 AllAll 各接口
 * - 未传：轮询 GET /generate/status/{taskId}（通用后端）
 */
export function pollGenerationStatus(taskId, callback, interval, options) {
  if (options && options.provider === 'official' && options.officialProfile) {
    try {
      var pollOfficial = options.officialMedia === 'music' ? pollOfficialMusic : pollOfficialVideo
      return pollOfficial(options.officialProfile, taskId, function (err, parsed) {
        if (callback && typeof callback === 'function') {
          if (err) return callback(err, null)
          var statusRes = {
            status:
              parsed.status === 'completed'
                ? 'completed'
                : parsed.status === 'failed'
                  ? 'failed'
                  : parsed.status,
            url: parsed.url,
            error: parsed.error,
            finalUnitDeduction: parsed.finalUnitDeduction,
            tracks: parsed.tracks,
            videoBlob: parsed.videoBlob || null,
            audioBlobs: parsed.audioBlobs != null ? parsed.audioBlobs : null
          }
          callback(null, statusRes)
        }
      }, interval, options)
    } catch (e) {
      if (callback && typeof callback === 'function') callback(e, null)
      return { stop: function () {} }
    }
  }
  if (options && options.provider === 'pollo') {
    return pollPolloStatus(taskId, function (err, parsed) {
      if (!callback || typeof callback !== 'function') return
      if (err) return callback(err, null)
      callback(null, {
        status: parsed.status === 'completed' ? 'completed' : parsed.status === 'failed' ? 'failed' : parsed.status,
        url: parsed.url,
        error: parsed.error
      })
    }, interval)
  }
  if (options && options.provider === 'allall') {
    if (options.allallApi === 'suno/music') {
      return pollSunoMusicStatus(taskId, function (err, parsed) {
        if (callback && typeof callback === 'function') {
          if (err) return callback(err, null)
          var statusRes = {
            status: parsed.status === 'completed' || parsed.status === 'success' ? 'completed' : parsed.status === 'failed' ? 'failed' : parsed.status,
            url: parsed.url,
            error: parsed.error,
            tracks: parsed.tracks
          }
          callback(null, statusRes)
        }
      }, interval)
    }
    var pollAllall = options.allallApi === 'video/generations' ? pollAllallWan25Status : (options.allallApi === 'mj' ? pollAllallMidjourneyStatus : pollAllallStatus)
    return pollAllall(taskId, function (err, parsed) {
      if (callback && typeof callback === 'function') {
        if (err) return callback(err, null)
        var statusRes = {
          status: parsed.status === 'completed' ? 'completed' : parsed.status === 'failed' ? 'failed' : parsed.status,
          url: parsed.url,
          error: parsed.error
        }
        callback(null, statusRes)
      }
    }, interval)
  }
  if (options && options.provider === 'wan-unified') {
    return pollWanUnifiedStatus(taskId, function (err, parsed) {
      if (!callback || typeof callback !== 'function') return
      if (err) return callback(err, null)
      callback(null, {
        status: parsed.status === 'completed' ? 'completed' : parsed.status === 'failed' ? 'failed' : parsed.status,
        url: parsed.url,
        urls: parsed.urls,
        tracks: parsed.tracks,
        audioBlobs: parsed.audioBlobs,
        coverBlobs: parsed.coverBlobs,
        error: parsed.error
      })
    }, interval, options)
  }
  var pollInterval = interval || 3000
  var pollTimer = null
  var stopped = false

  function poll() {
    if (stopped) return
    apiClient.get('/generate/status/' + taskId)
      .then(function(response) {
        if (stopped) return
        if (callback && typeof callback === 'function') {
          callback(null, response)
        }
        if (String(response.status) === 'completed' || String(response.status) === 'failed') {
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function(error) {
        if (stopped) return
        if (callback && typeof callback === 'function') {
          callback(error, null)
        }
        stop()
      })
  }

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  poll()

  return {
    stop: stop
  }
}

export default generationApi
